import React, { FC } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import styles from "./legal.module.css";
import { Link } from "@remix-run/react";

function stringConverter(str: string): string {
  if (!str) return "";

  // Remove first digit, first space, and first period
  str = str
    .replace(/\d/, "") // remove first number
    .replace(" ", "") // remove first space
    .replace(".", ""); // remove first period

  // Convert to URL-friendly slug
  return str
    .toLowerCase()
    .normalize("NFD") // split accented characters
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-z0-9\s-]/g, "") // remove special chars except space and hyphen
    .trim() // trim leading/trailing spaces
    .replace(/\s+/g, "-") // replace spaces with -
    .replace(/-+/g, "-"); // collapse multiple dashes
}

// Custom hook to handle smooth scrolling to a referenced heading element.
// Here we use HTMLHeadingElement, since we use the hook only on <h1>, <h2>, or <h3>.
// function useScrollIntoView() {
//   const headingRef = useRef<HTMLHeadingElement>(null);

//   const handleClick = useCallback(() => {
//     if (headingRef.current) {
//       headingRef.current.scrollIntoView({ behavior: "smooth" });
//     }
//   }, []);

//   return { headingRef, handleClick };
// }

interface LegalProps {
  content: string;
}

interface HeadingProps extends React.HTMLAttributes<HTMLElement> {
  as: keyof JSX.IntrinsicElements;
  children: React.ReactNode;
}

const Heading: FC<HeadingProps> = ({ as: Tag, children, className, ...props }) => {
  const id = typeof children === "string" ? stringConverter(children.toString()) : undefined;

  return React.createElement(
    Tag,
    { id, className: `${className} group`, ...props },
    <>
      <Link
        to={`#${id}`}
        viewTransition={true}
        className="no-underline text-inherit hover:underline"
      >
        {children}
      </Link>
    </>,
  );
};

const H1: FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, ...props }) => (
  <Heading as="h1" className="text-primary text-2xl md:text-3xl py-2 font-semibold" {...props}>
    {children}
  </Heading>
);

const H2: FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, ...props }) => (
  <Heading as="h2" className="text-primary text-xl py-2 font-semibold" {...props}>
    {children}
  </Heading>
);

// For H3, we additionally ensure the clickable element is keyboard accessible.
// const H3: FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, ...props }) => {
//   const { headingRef, handleClick } = useScrollIntoView();
//   const id = typeof children === "string" ? stringConverter(children.toString()) : undefined;

//   return (
//     <h3 id={id} className="text-primary text-lg font-semibold" ref={headingRef} {...props}>
//       <div
//         onClick={handleClick}
//         onKeyDown={(e) => {
//           if (e.key === "Enter" || e.key === " ") handleClick();
//         }}
//         role="button"
//         tabIndex={0}
//         aria-hidden="true"
//       >
//         {children}
//       </div>
//     </h3>
//   );
// };

const H3: FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ children, ...props }) => (
  <Heading as="h3" className="text-primary text-lg font-semibold" {...props}>
    {children}
  </Heading>
);

const Code: FC<React.HTMLAttributes<HTMLElement>> = ({ children, ...props }) => {
  return (
    <code className="break-words" {...props}>
      {children}
    </code>
  );
};

// A custom list item component with improved accessibility and basic type safety.
interface CustomLiProps {
  children?: React.ReactNode;
  node?: any;
}

const LI: FC<CustomLiProps> = ({ children, node }) => {
  const isOrdered = node?.parent?.tagName === "ol";

  return (
    <li className="flex items-center">
      {!isOrdered && (
        <svg
          className="w-3.5 h-3.5 me-2 text-primary flex-shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
        </svg>
      )}
      {children}
    </li>
  );
};

const OL: FC<React.OlHTMLAttributes<HTMLOListElement>> = ({ children, ...props }) => {
  return (
    <ol type="a" {...props}>
      {children}
    </ol>
  );
};

const UL: FC<React.HTMLAttributes<HTMLUListElement>> = ({ children, ...props }) => {
  return (
    <ul className="list-disc list-inside" {...props}>
      {children}
    </ul>
  );
};

const BR: FC = () => {
  return <br />;
};

// Mapping of custom components to use with ReactMarkdown.
const components: Components = {
  h1: H1,
  h2: H2,
  h3: H3,
  li: LI,
  br: BR,
  ol: OL,
  ul: UL,
  code: Code,
};

const Legal: FC<LegalProps> = ({ content }) => {
  // Generating the Table of Contents from headings using regex.
  // For a more robust solution consider using an AST parser.
  const headings = content.match(/^#{1,2}\s+(.+)/gm);

  return (
    <div className={`markdown ${styles.container}`}>
      <div
        className={` bg-zinc-100 dark:bg-zinc-800 text-zinc-950 dark:text-white ${styles.sidebar}`}
      >
        <h3 className="text-primary">Contents</h3>
        <ul>
          {headings &&
            headings.map((heading: string, index: React.Key | null | undefined) => {
              // Remove all markdown '#' characters and trim whitespace.
              const title = heading.replace(/#{1,}\s*/g, "").trim();
              const anchor = stringConverter(title);
              return (
                <li key={index}>
                  <Link
                    to={`#${anchor}`}
                    viewTransition={true}
                    className="hover:underline hover:text-primary text-zinc-950 dark:text-white"
                  >
                    {title}
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
      <div
        className={`${styles.contentWrapper} text-zinc-900 dark:text-white py-2 container mx-auto`}
      >
        <ReactMarkdown className="text-wrap " components={components}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default Legal;
